import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement
} from 'react';
import { shallow } from 'zustand/shallow';
import {
  BillingRegistryTrialNoticeKind as Kind,
  BillingRegistryTrialNoticeRequest,
  BillingRegistryTrialNoticeOperation as Operation,
  BillingRegistryPlanPurchaseState as PurchaseState
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import { AgentRegistryMembershipRole as Role } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import { BillingRegistryTrialNotice } from '@tryiris-ai/mailroom-helper/billing';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { config } from '#~/config/index.js';
import { useAnalytics } from '#~/analytics/hooks/use-analytics.js';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { EVENT_CATEGORIES } from '#~/analytics/constants/event-categories.js';
import { useDepot } from '#~/depot/hooks.js';
import { nats } from '#~/nats/index.js';
import { toIdFromHex } from '#~/api/utils.js';
import {
  forceRefreshBilling,
  getTrialNoticeBillingRead,
  subscribeTrialNoticeBilling
} from '#~/api/billing/limits.js';
import { usePlanPurchases } from '#~/api/billing/use-plan-purchase.js';
import {
  StandardTrialNoticeController,
  type DisplayedTrialNotice,
  type UnpublishedTrialNoticePermission
} from '#~/api/billing/trial-notice-controller.js';
import { useDeviceIds, useRoom } from '#~/room/hooks.js';
import type { Room } from '@studio/models/room';
import { useSettings } from '../../hooks.js';
import { Panel } from '../types.js';
import { StandardTrialExpiryNotice } from './standard-trial-expiry-notice.jsx';
import { StandardTrialWarningNotice } from './standard-trial-warning-notice.jsx';

// Native onboarding occupies the browser's top layer without an explicit role.
// Reserving a notice underneath it can strand an unseen notice and block Skip.
// This presentation gate never changes billing, roles or camera enforcement.
const displayAvailable = (): boolean =>
  document.visibilityState === 'visible' &&
  document.querySelector('dialog[open], [role="dialog"]:not([data-trial-notice])') === null;

// A route remount in this loaded page retains only never-published permissions;
// primary shared storage still decides eligibility,
// other-device exclusion and actual display acknowledgement. No token is kept.
const unpublishedPermissions = new Map<string, UnpublishedTrialNoticePermission>();
const unpublishedPermissionListeners = new Set<
  (permission: UnpublishedTrialNoticePermission) => void
>();

/** Mounted once in the actual Studio route, in both browser and Electron. */
export const StandardTrialNoticeRoot = ({
  roomId
}: {
  readonly roomId: Room['id'];
}): ReactElement | null => {
  const auth = useAuth0();
  const { track } = useAnalytics();
  const recordedChoice = useRef<string | null>(null);
  const { purchases, actorKey, isCurrentAccount } = usePlanPurchases();
  // Native auth settles before the mapped Iris profile. Keep this local notice
  // namespace tied to the authenticated subject; no subject is sent to the API.
  const actorID = actorKey ?? null;
  const requestAuth = useRef({ getToken: auth.getAccessTokenSilently, isCurrentAccount });
  requestAuth.current = { getToken: auth.getAccessTokenSilently, isCurrentAccount };
  const settings = useSettings();
  const [role, bootReady] = useDepot(
    (depot) =>
      [
        depot.roomRecords[roomId]?.permissions.role,
        depot.boot.observation?.roomId === roomId && depot.boot.observation?.outcome === 'ready'
      ] as const,
    shallow
  );
  const [billing, setBilling] = useState(getTrialNoticeBillingRead);
  const [available, setAvailable] = useState(displayAvailable);
  const [view, setView] = useState<DisplayedTrialNotice | null>(null);
  const [controller] = useState(
    () =>
      new StandardTrialNoticeController({
        request: async (studio, operation, kind, commandId, displayId) => {
          // Capture this request's account check. A new render must not bless
          // an older token or response by replacing it with the new account's.
          const account = requestAuth.current;
          if (!account.isCurrentAccount()) throw new Error('trial notice account changed');
          const id = toIdFromHex(studio);
          if (!id) throw new Error('trial notice Studio identity is unresolved');
          const credentials = await account.getToken();
          if (!account.isCurrentAccount()) throw new Error('trial notice account changed');
          const response = await BillingRegistryTrialNotice.request(
            nats.cloudConnection(),
            BillingRegistryTrialNoticeRequest.fromPartial({
              roomID: id,
              token: credentials,
              operation,
              commandId,
              ...(kind === undefined ? {} : { kind }),
              ...(displayId === undefined ? {} : { displayId })
            })
          );
          if (!account.isCurrentAccount() && operation !== Operation.TRIAL_NOTICE_AUTHORIZE_DISPLAY)
            throw new Error('trial notice account changed');
          // An already issued permission belongs to the captured old account.
          // The controller retains it only in that account's unused namespace,
          // then requires a fresh authenticated observation before publication.
          return response;
        },
        canDisplay: displayAvailable,
        uuid: () => crypto.randomUUID(),
        monotonicNow: () => performance.now(),
        publish: setView,
        unpublishedPermissions,
        unpublishedPermissionListeners
      })
  );
  const { videoIds } = useDeviceIds();
  const devices = useRoom((room) => room.devices);
  const cameras = videoIds.flatMap((id) => {
    const device = devices[id];
    return device ? [{ agentId: device.agentId, deviceId: device.id, name: device.name }] : [];
  });

  useEffect(() => subscribeTrialNoticeBilling(setBilling), []);
  useLayoutEffect(() => {
    controller.activate();
    return () => controller.dispose();
  }, [controller]);
  useLayoutEffect(() => {
    // Fresh authenticated confirmation closes notices before paid access refresh.
    // Saved receipts remain unverified after reload and cannot suppress a notice.
    if (
      purchases[roomId]?.receiptVerified === true &&
      purchases[roomId]?.receipt?.state === PurchaseState.PURCHASE_STATE_CONFIRMED
    )
      controller.confirmedPaid(roomId);
  }, [controller, purchases, roomId]);
  useLayoutEffect(() => {
    if (config.vendor !== 'iris' && config.vendor !== 'ptzoptics') return;
    void controller.setContext({
      roomID: roomId,
      actorID,
      role,
      ready: isCurrentAccount() && bootReady && available,
      billing
    });
  }, [controller, roomId, actorID, role, bootReady, available, billing, isCurrentAccount]);

  useEffect(() => {
    const updateAvailability = () => setAvailable(displayAvailable());
    const focus = () => {
      updateAvailability();
      // Reuse authoritative billing refresh for offline return. It fences
      // Studio changes; opening a route or beginning this read consumes nothing.
      void forceRefreshBilling(roomId);
      void controller.refresh();
    };
    const visibility = () => {
      updateAvailability();
      if (document.visibilityState === 'visible') focus();
    };
    const observer = new MutationObserver(updateAvailability);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      // Closing a native dialog may change only this attribute, not the tree.
      attributeFilter: ['role', 'open']
    });
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      observer.disconnect();
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [controller, roomId]);
  useEffect(() => {
    if (!view) return;
    // Other devices have no billing-specific push. While visible, a bounded
    // fresh primary observation closes paid notices within the poll interval.
    const timer = setInterval(() => {
      void controller.refresh();
    }, 5000);
    return () => clearInterval(timer);
  }, [controller, view !== null]);

  const displayId = view?.notice.displayId;
  const currentDisplay = useRef({ actorID, roomId, role, view });
  currentDisplay.current = { actorID, roomId, role, view };
  const isCurrentDisplay = useCallback(() => {
    const current = currentDisplay.current;
    return (
      isCurrentAccount() &&
      displayId !== undefined &&
      current.actorID === actorID &&
      current.roomId === roomId &&
      current.role !== undefined &&
      current.role !== Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED &&
      current.view?.actorID === actorID &&
      current.view?.roomID === roomId &&
      current.view?.notice.displayId === displayId
    );
  }, [actorID, displayId, isCurrentAccount, roomId]);
  const shown = useCallback(() => {
    if (displayId && isCurrentDisplay()) controller.reportShown(displayId);
  }, [controller, displayId, isCurrentDisplay]);
  const dismiss = useCallback(() => {
    if (isCurrentDisplay()) controller.dismiss();
  }, [controller, isCurrentDisplay]);
  const elapsed = useCallback(() => {
    if (!isCurrentDisplay()) return;
    controller.dismiss();
    // Elapsed monotonic duration can close a warning; only a fresh server read
    // can subsequently qualify an expiry notice.
    void forceRefreshBilling(roomId);
  }, [controller, roomId, isCurrentDisplay]);
  useEffect(() => {
    if (!view) return;
    // A failed or hung observation cannot keep a served notice beyond its
    // window. Only elapsed monotonic time closes it; no client clock grants one.
    let timer: ReturnType<typeof setTimeout>;
    const checkDeadline = () => {
      const remaining =
        view.notice.windowEnd * 1000 -
        view.notice.serverNowMs -
        (performance.now() - view.receivedAt);
      if (remaining <= 0) elapsed();
      else timer = setTimeout(checkDeadline, Math.min(remaining, 2147483647));
    };
    checkDeadline();
    return () => clearTimeout(timer);
  }, [view, elapsed]);
  const recordChoice = useCallback(
    (event: typeof EVENT_NAMES.CLICK_UPGRADE_PLAN | typeof EVENT_NAMES.CLICK_STAY_ON_FREE) => {
      if (
        !isCurrentDisplay() ||
        view?.notice.kind !== Kind.TRIAL_NOTICE_EXPIRY ||
        recordedChoice.current === displayId
      )
        return;
      recordedChoice.current = displayId ?? null;
      const product = config.vendor === 'ptzoptics' ? 'hive' : 'iris';
      // Only an explicit button choice counts. X, Escape and elapsed notices do
      // not mean Stay on Free, and opening the picker does not prove payment.
      track(event, EVENT_CATEGORIES.BILLING, {
        studio_id: roomId,
        trial_id: `${product}:${roomId}:${view.notice.trialEnd}`,
        trial_end: new Date(view.notice.trialEnd * 1000).toISOString(),
        product,
        environment: config.env,
        source: 'trial_expiry_notice'
      });
    },
    [displayId, isCurrentDisplay, roomId, track, view]
  );
  const stayFree = useCallback(() => recordChoice(EVENT_NAMES.CLICK_STAY_ON_FREE), [recordChoice]);
  const upgrade = useCallback(() => {
    if (
      !isCurrentDisplay() ||
      currentDisplay.current.role !== Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER ||
      currentDisplay.current.view?.notice.canUpgrade !== true
    )
      return;
    recordChoice(EVENT_NAMES.CLICK_UPGRADE_PLAN);
    controller.dismiss();
    settings(Panel.Billing, roomId, { openPlanPicker: true });
  }, [controller, roomId, settings, isCurrentDisplay, recordChoice]);
  // Context effects run after a commit. Bind the rendered view to the exact
  // requesting identity and role now, so a switch cannot paint stale controls.
  if (
    !view ||
    !displayId ||
    !isCurrentAccount() ||
    view.roomID !== roomId ||
    view.actorID !== actorID ||
    role === undefined ||
    role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED
  )
    return null;
  if (view.notice.kind === Kind.TRIAL_NOTICE_REMINDER)
    return (
      <StandardTrialWarningNotice
        key={displayId}
        roomId={roomId}
        view={view}
        onShown={shown}
        onDismiss={dismiss}
        onElapsed={elapsed}
      />
    );
  if (view.notice.kind === Kind.TRIAL_NOTICE_EXPIRY)
    return (
      <StandardTrialExpiryNotice
        key={displayId}
        roomId={roomId}
        studioName={view.notice.studioName}
        cameras={cameras}
        onStayFree={stayFree}
        onShown={shown}
        onDismiss={dismiss}
        // Trial explanations reach every member, but only the current owner
        // receives purchase controls. Ordinary billing permissions are unchanged.
        {...(role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER && view.notice.canUpgrade === true
          ? { onUpgrade: upgrade }
          : {})}
      />
    );
  return null;
};
